// @flow
import React, { Component, Fragment } from 'react';
import BidConsoleOpener from '../../components/BidConsoleOpener/BidConsoleOpener';
import Button from '@liveauctioneers/caterwaul-components/lib/Button/Button';
import CardCtaBanner from './CardCtaBanner';
import FormattedCurrencyContainer from '../../components/Formatted/FormattedCurrencyContainer';
import { FormattedMessage } from 'react-intl';
import isEqual from 'react-fast-compare';
import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import RegisterLinkContainer from '../../components/RegisterLink/RegisterLinkContainer';
import ShouldLogInContainer from '../../components/ShouldLogIn/ShouldLogInContainer';
import { shouldShowLive } from '../../utils/live';
import styles from './CardCta.module.scss';
import Throbber from '@liveauctioneers/caterwaul-components/lib/Throbber/Throbber';
import WhyCantRetractBidPopover from '../../components/Popovers/WhyCantRetractBidPopover';

type Props = {
    accountInGoodStanding: boolean,
    allowBidding: boolean,
    approved: boolean,
    bidderHasHighBid: boolean,
    bidderMaxBid: number,
    bidLimit: number,
    bidLimitBalance: number,
    blockedByAuctioneer: boolean,
    catalogId: number,
    catalogStatus: string,
    currency: string,
    currentAskPrice: number,
    declined: boolean,
    isAvailable: boolean,
    isCatalogOnly: boolean,
    isLotOpen: boolean,
    itemId: number,
    itemUrl: string,
    leadingBid: number,
    leadingBidder: boolean,
    limited: boolean,
    openAbsenteeBidModal: Function,
    openRegisterForAuctionModal: Function,
    openRetractBidModal: Function,
    pending: boolean,
    registered: boolean,
    salePrice: number,
    saleStartTs: number,
    sellerId: number,
    showBanner: boolean,
    showCta: boolean,
    showSendSellerMessageModal: Function,
    startPrice: number,
    submitLiveBid: Function,
    submitted: boolean,
    suspended: boolean,
};

export default class CardCta extends Component<Props> {
    static defaultProps = {
        allowBidding: false,
        approved: false,
        bidderHasHighBid: false,
        bidderMaxBid: 0,
        bidLimit: 0,
        blockedByAuctioneer: false,
        catalogStatus: '',
        currency: 'USD',
        currentAskPrice: 0,
        declined: false,
        limited: false,
        registered: false,
        salePrice: 0,
        showBanner: false,
    };

    shouldComponentUpdate = (nextProps: Props) => !isEqual(this.props, nextProps);

    openBidModal = () => {
        this.props.openAbsenteeBidModal(this.props.itemId, 0);
    };

    openRetractBidModal = () => {
        this.props.openRetractBidModal(this.props.itemId);
    };

    linkAndButton = () => {
        const {
            accountInGoodStanding,
            bidderHasHighBid,
            bidderMaxBid,
            blockedByAuctioneer,
            catalogId,
            catalogStatus,
            currency,
            declined,
            isCatalogOnly,
            itemUrl,
            saleStartTs,
            sellerId,
            showSendSellerMessageModal,
            suspended,
        } = this.props;

        const bidAmount = <FormattedCurrencyContainer value={bidderMaxBid} sourceCurrency={currency} noFraction />;
        const showLiveButton = shouldShowLive(catalogStatus, isCatalogOnly, saleStartTs);
        let link = '';
        if (blockedByAuctioneer || suspended || !accountInGoodStanding) {
            link = (
                <Link
                    to="https://help.liveauctioneers.com/article/146-how-do-i-restore-my-suspended-account"
                    className={styles['help-button']}
                >
                    <FormattedMessage id="suspendedBidderBanner.button" />
                </Link>
            );
        } else if (declined) {
            link = (
                <ShouldLogInContainer requireLogin requireCompleteProfile runFunction={showSendSellerMessageModal}>
                    <Link>
                        <FormattedMessage id="contact_auctioneer" />
                    </Link>
                </ShouldLogInContainer>
            );
        } else if (catalogStatus === 'done') {
            link = (
                <Link to={itemUrl} route>
                    <FormattedMessage id="view_similar_items" />
                </Link>
            );
        } else if (showLiveButton) {
            link = (
                <BidConsoleOpener catalogId={catalogId} sellerId={sellerId}>
                    <Link className={styles.live}>
                        <FormattedMessage id="enter_live_auction" />
                    </Link>
                </BidConsoleOpener>
            );
        } else {
            link = (
                <Link onClick={this.openBidModal}>
                    {bidderHasHighBid ? (
                        <FormattedMessage id="increase_max_of" values={{ bidAmount }} />
                    ) : (
                        <FormattedMessage id="increase_bid_of" values={{ bidAmount }} />
                    )}
                </Link>
            );
        }

        let retractButton;
        if (catalogStatus === 'online') {
            retractButton = (
                <span className={styles.retract} onClick={this.openRetractBidModal}>
                    <FormattedMessage id="retract" />
                </span>
            );
        } else if (catalogStatus === 'live') {
            retractButton = (
                <span className={styles.retract}>
                    <WhyCantRetractBidPopover id="whyCantRetractBidPopover" styles={styles} />
                </span>
            );
        } else {
            retractButton = <span className={styles.retract} />;
        }
        return (
            <Fragment>
                {link}
                {retractButton}
            </Fragment>
        );
    };

    placeBid = () => {
        const {
            accountInGoodStanding,
            approved,
            catalogId,
            currency,
            currentAskPrice,
            itemId,
            limited,
            submitLiveBid,
        } = this.props;

        // dont only rely on the "disabled" button to prevent bidders who havent been approved
        if (accountInGoodStanding && (approved || limited)) {
            submitLiveBid(Date.now(), catalogId, currentAskPrice, currency, itemId);
        } else {
            return false;
        }
    };

    render() {
        const {
            accountInGoodStanding,
            allowBidding,
            approved,
            bidLimit,
            bidLimitBalance,
            blockedByAuctioneer,
            catalogId,
            catalogStatus,
            currency,
            currentAskPrice,
            declined,
            isAvailable,
            isCatalogOnly,
            isLotOpen,
            leadingBid,
            leadingBidder,
            limited,
            pending,
            registered,
            salePrice,
            showBanner,
            showCta,
            startPrice,
            submitted,
            suspended,
        } = this.props;

        const currentPrice = leadingBid || salePrice || startPrice || 0;
        const overBidLimit = bidLimitBalance + currentPrice > bidLimit;

        const isStoppedFromBidding =
            !accountInGoodStanding ||
            (!approved && !limited) ||
            suspended ||
            declined ||
            blockedByAuctioneer ||
            overBidLimit;

        return (
            <div className={styles['item-card-control']}>
                {isLotOpen && allowBidding ? (
                    <Fragment>
                        {!isStoppedFromBidding && registered && !leadingBidder && (
                            <Button primary red small onClick={this.placeBid} disabled={submitted}>
                                {submitted ? (
                                    <Throbber colorWhite />
                                ) : (
                                    <FormattedMessage
                                        id="bidding_console.console.button.bid"
                                        values={{
                                            amount: (
                                                <FormattedCurrencyContainer
                                                    value={currentAskPrice}
                                                    sourceCurrency={currency}
                                                    noFraction
                                                />
                                            ),
                                        }}
                                    />
                                )}
                            </Button>
                        )}
                        {leadingBidder && (
                            <Button primary small className={styles.winning}>
                                <FormattedMessage id="winning" />
                            </Button>
                        )}
                        {!registered && (
                            <RegisterLinkContainer
                                className={styles['register-link']}
                                catalogId={catalogId}
                                isCatalogOnly={isCatalogOnly}
                            />
                        )}
                    </Fragment>
                ) : showCta ? (
                    this.linkAndButton()
                ) : showBanner && catalogStatus !== 'done' ? (
                    <CardCtaBanner
                        approved={approved}
                        catalogId={catalogId}
                        declined={declined}
                        isAvailable={isAvailable}
                        isCatalogOnly={isCatalogOnly}
                        pending={pending}
                        registered={registered}
                    />
                ) : null}
            </div>
        );
    }
}
